import React, { FunctionComponent, useMemo } from 'react';
import LottieComponent, { LottieComponentProps } from 'lottie-react';
import AfterPurchaseLoader from './animations/after-purchase-loader.json';
import EmptyCart from './animations/empty-cart.json';
import FiversSpinner from './animations/fivers-spinner.json';
import NotificationCleared from './animations/notification-cleared.json';
import NotificationError from './animations/notification-error.json';
import NotificationLoading from './animations/notification-loading.json';
import TtpSpinner from './animations/ttp-spinner.json';

export interface ColorMapping {
  /** Original color in [r, g, b, a] format (0-1 range) */
  original: [number, number, number, number?];
  /** New color in [r, g, b, a] format (0-1 range) */
  replacement: [number, number, number, number?];
}

interface Props extends Omit<LottieComponentProps, 'animationData'> {
  type: LottieTypes;
  /** Optional color mappings to replace colors in the animation based on theme */
  colorMappings?: ColorMapping[];
}

export enum LottieTypes {
  AfterPurchaseLoader = 'after-purchase-loader',
  FiversSpinner = 'fivers-spinner',
  TtpSpinner = 'ttp-spinner',
  NotificationCleared = 'notification-cleared',
  NotificationError = 'notification-error',
  NotificationLoading = 'notification-loading',
  EmptyCart = 'empty-cart',
}

const LottieAnimation = {
  [LottieTypes.AfterPurchaseLoader]: AfterPurchaseLoader,
  [LottieTypes.FiversSpinner]: FiversSpinner,
  [LottieTypes.TtpSpinner]: TtpSpinner,
  [LottieTypes.NotificationCleared]: NotificationCleared,
  [LottieTypes.NotificationError]: NotificationError,
  [LottieTypes.NotificationLoading]: NotificationLoading,
  [LottieTypes.EmptyCart]: EmptyCart,
};

/**
 * Type guard to check if a value is a plain object
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

/**
 * Type guard to check if an array is a color array [r, g, b, a?]
 */
const isColorArray = (
  arr: unknown[],
): arr is [number, number, number, number?] => {
  return (
    arr.length >= 3 &&
    arr.length <= 4 &&
    arr.every((val) => typeof val === 'number' && val >= 0 && val <= 1)
  );
};

/**
 * Recursively replaces colors in the animation data
 */
const replaceColors = (
  obj: unknown,
  colorMappings: ColorMapping[],
): unknown => {
  if (typeof obj !== 'object' || obj === null) return obj;

  if (Array.isArray(obj)) {
    // Check if this is a color array [r, g, b, a?]
    if (isColorArray(obj)) {
      // Try to match with color mappings
      for (const mapping of colorMappings) {
        const [r, g, b] = mapping.original;
        const tolerance = 0.01;

        // Check RGB match
        const rgbMatches =
          Math.abs(obj[0] - r) < tolerance &&
          Math.abs(obj[1] - g) < tolerance &&
          Math.abs(obj[2] - b) < tolerance;

        if (!rgbMatches) continue;

        // Check alpha only if mapping.original specifies it
        if (mapping.original.length >= 4) {
          const a = mapping.original[3]!;
          const alphaMatches =
            obj[3] === undefined || Math.abs(obj[3] - a) < tolerance;
          if (!alphaMatches) continue;
        }

        // Match found - preserve alpha channel if the matched color has one but replacement doesn't
        if (mapping.replacement.length === 3 && obj.length === 4) {
          return [...mapping.replacement, obj[3]];
        }
        return [...mapping.replacement];
      }
    }
    return obj.map((item: unknown) => replaceColors(item, colorMappings));
  }

  if (isPlainObject(obj)) {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = replaceColors(obj[key], colorMappings);
      }
    }
    return result;
  }

  return obj;
};

export const Lottie: FunctionComponent<Props> = ({
  type,
  colorMappings,
  ...props
}) => {
  const baseAnimationData = LottieAnimation[type];

  // Apply color mappings if provided
  const animationData = useMemo(() => {
    if (!colorMappings || colorMappings.length === 0) {
      return baseAnimationData;
    }

    // Deep clone and replace colors
    return replaceColors(
      JSON.parse(JSON.stringify(baseAnimationData)),
      colorMappings,
    );
  }, [baseAnimationData, colorMappings]);

  return <LottieComponent {...props} animationData={animationData} />;
};
